import React, { useEffect, useState, useCallback, useMemo } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import IotPartnerTable from "../../components/Table/IotPartnerTable";
import { dataStateType } from "src/constants/status";
import Pagination from "src/components/Pagination/Pagination";
import ControlPanelSystem from "src/components/controlPanelSystem/ControlPanelSystem";
import useControlPanel from "src/hooks/useControlPanel";
import FLFIotPartnerControlPanel from "../../components/ControlPanel/FLFIotPartnerControlPanel";
import {
  createFLFIotPartner,
  getFLFIotPartner,
  removeFLFIotPartner,
  sortFLFIotPartner,
  updateFLFIotPartner,
} from "src/apis/FLF/iotPartner.api";
import { compareTwoArrayDnD } from "src/utils/helper";
import { NotifyConfirm, Toast } from "src/components/notify/Notify";
import useCurrentActionCache from "src/hooks/useCurrentActionCache";
import { CACHE_IOT_PARTNER } from "src/constants/actionCache";
import { path } from "src/constants/path";

const initIotPartnerData = {
  list: [],
  total_record: 0,
};

const titlePage = "Iot Partner FPT Life";

const FLFIotPartner = () => {
  const {
    page,
    searchKey,
    numberPage,
    setNumberPage,
    limit,
    setLimit,
    status,
    setStatus,
    startIndex,
    endIndex,
    setStartDate,
    setEndDate,
    handleInputChange,
    handleChangePage,
  } = useControlPanel();

  const { currentActionCache, setCurrentActionCache } = useCurrentActionCache();
  const [iotPartnerData, setIotPartnerData] = useState(initIotPartnerData);
  const [filterIotPartners, setFilterIotPartners] = useState([]);
  const [listIotPartnerId, setListIotPartnerId] = useState([]);
  const [queueSortApi, setQueueSortApi] = useState([]);
  const [isSorted, setIsSorted] = useState(false);
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [errorsForm, setErrorsForm] = useState({});

  const fetchIotPartners = useCallback(() => {
    getFLFIotPartner({}, (result) => {
      setIotPartnerData(result);
      setFilterIotPartners(result.list);
    });
  }, []);

  useEffect(() => {
    fetchIotPartners();
  }, [fetchIotPartners]);

  useEffect(() => {
    setNumberPage(page);
  }, [page, setNumberPage]);

  const updateFilteredPartners = useCallback((partners) => {
    setFilterIotPartners(partners);
    setListIotPartnerId(partners.map((partner) => partner.id));
  }, []);

  /* ---- handle search ----- */
  const searchByKeyWord = useCallback(
    (partners, key) => {
      if (status === dataStateType.INACTIVE) {
        return [];
      }
      return partners.filter((partner) =>
        partner.partnerName.toLowerCase().includes(key.toLowerCase().trim())
      );
    },
    [status]
  );

  const handleSearch = useCallback(
    (value) => {
      const filteredPartners = value
        ? searchByKeyWord(iotPartnerData.list, value)
        : iotPartnerData.list;
      updateFilteredPartners(filteredPartners);
    },
    [iotPartnerData.list, searchByKeyWord, updateFilteredPartners]
  );

  useEffect(() => {
    handleSearch(searchKey);
  }, [searchKey, handleSearch]);

  /* ---- handle add new and update ----- */
  const addOrEdit = useCallback(
    (iotPartner, resetForm) => {
      if (iotPartner.id) {
        // Update existing partner
        const payload = { partnerName: iotPartner.partnerName };
        updateFLFIotPartner(iotPartner.id, payload, handleApiResponse);
      } else {
        // Create new partner
        createFLFIotPartner(iotPartner, handleApiResponse);
      }

      function handleApiResponse(result) {
        if (result.data?.status === 422) {
          setErrorsForm(result.data.data);
        } else {
          fetchIotPartners();
          resetForm();
          setRecordForEdit(null);
          setOpenPopup(false);
          setErrorsForm({});
          setCurrentActionCache(CACHE_IOT_PARTNER);
        }
      }
    },
    [fetchIotPartners, setCurrentActionCache]
  );

  /* ---- handle sort and DnD ----- */
  const handleSort = useCallback(() => {
    sortFLFIotPartner({ queueSort: queueSortApi }, () => {
      setQueueSortApi([]);
      setIsSorted(false);
      setCurrentActionCache(CACHE_IOT_PARTNER);
      fetchIotPartners();
    });
  }, [queueSortApi, setCurrentActionCache, fetchIotPartners]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    // kiểm tra nếu không tồn tại over (kéo quá size thì return tránh lỗi)
    if (!over) return;

    if (active.id !== over.id) {
      // lấy vị trí cũ (từ thằng active)
      const oldIndex = filterIotPartners.findIndex((c) => c.id === active.id);

      // lấy vị trí mới (từ thằng order)
      const newIndex = filterIotPartners.findIndex((c) => c.id === over.id);

      const newOrder = arrayMove(filterIotPartners, oldIndex, newIndex);
      const newOrderIds = newOrder.map((c) => c.id);

      // check xem có thay đổi thì hiện nút xác nhận cập nhật
      const isOrderChanged = !compareTwoArrayDnD(listIotPartnerId, newOrderIds);
      setFilterIotPartners([...newOrder]);

      if (isOrderChanged) {
        setIsSorted(true);
        setQueueSortApi((prev) => [
          ...prev,
          {
            activeIotPartnerId: active.id,
            overIotPartnerId: over.id,
          },
        ]);
      } else {
        setIsSorted(false);
        setQueueSortApi([]);
      }
    }
  };

  const handleDelete = useCallback(
    (iotPartnerDelete) => {
      if (iotPartnerDelete.devices.length) {
        Toast("warning", "Iot partner vẫn còn thiết bị, không thể xóa");
      } else {
        NotifyConfirm("warning", "Are you delete it ?", (result) => {
          if (result.isConfirmed) {
            removeFLFIotPartner(
              iotPartnerDelete.id,
              setCurrentActionCache(CACHE_IOT_PARTNER)
            );
            setIotPartnerData((prev) => ({
              ...prev,
              list: prev.list.filter(
                (partner) => partner.id !== iotPartnerDelete.id
              ),
              total_record: prev.total_record - 1,
            }));
            updateFilteredPartners((prev) =>
              prev.filter((partner) => partner.id !== iotPartnerDelete.id)
            );
          }
        });
      }
    },
    [updateFilteredPartners, setCurrentActionCache]
  );

  const handleOpenPopup = useCallback((isOpen, item = null) => {
    setOpenPopup(isOpen);
    setRecordForEdit(item);
    setErrorsForm({});
  }, []);

  /* ---- result for render ----- */
  const resultFilter = useMemo(
    () => searchByKeyWord(filterIotPartners, searchKey),
    [filterIotPartners, searchKey, searchByKeyWord]
  );
  const listIotPartnerRender = useMemo(
    () => resultFilter.slice(startIndex, endIndex),
    [resultFilter, startIndex, endIndex]
  );

  return (
    <div>
      <div className="page-header">
        <h2>{titlePage}</h2>
      </div>
      {/** control pan */}
      <FLFIotPartnerControlPanel
        handleOpenPopup={handleOpenPopup}
        isSorted={isSorted}
        handleSort={handleSort}
        currentActionCache={currentActionCache}
        recordForEdit={recordForEdit}
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
        addOrEdit={addOrEdit}
        errorsForm={errorsForm}
      >
        <ControlPanelSystem
          searchKey={searchKey}
          handleInputChange={(e) => handleInputChange(e, handleSearch)}
          limit={limit}
          setLimit={setLimit}
          setNumberPage={setNumberPage}
          status={status}
          setStatus={setStatus}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          isDisplayDatePicker={false}
        />
      </FLFIotPartnerControlPanel>
      {/** table */}
      <IotPartnerTable
        data={listIotPartnerRender}
        handleOpenPopup={handleOpenPopup}
        handleDelete={handleDelete}
        status={dataStateType.ACTIVE}
        onDragEnd={handleDragEnd}
      />
      {/** Pagination */}
      <Pagination
        pathname={path.flfIotPartner}
        page={numberPage}
        handleChangePage={(page) => handleChangePage(page)}
        limit={limit}
        pageSize={resultFilter.length}
      />
    </div>
  );
};

export default FLFIotPartner;
